<template>
  <section v-if="type === 'heroNumber'" class="relative overflow-hidden rounded-[28px] bg-[#eef6f6] text-center shadow-[0_16px_34px_-18px_rgba(28,72,72,0.45)]">
    <img v-if="a.image" :src="a.image" alt="" class="absolute inset-0 h-full w-full object-cover" style="-webkit-mask-image:linear-gradient(transparent 20%,#000 62%);mask-image:linear-gradient(transparent 20%,#000 62%)" loading="lazy">
    <div class="relative px-[24px] pb-[170px] pt-[24px]">
      <p v-if="a.kicker" dir="auto" class="text-[12px] font-semibold uppercase tracking-[1.500px] text-[#1c4848]/75">{{ a.kicker }}</p>
      <p dir="auto" class="reader-serif mt-[2px] text-[72px] font-bold leading-[80px] text-[#1c4848]">{{ a.value }}</p>
      <p v-if="a.unit" dir="auto" class="text-[13px] font-bold uppercase tracking-[1.500px] text-[#1c4848]">{{ a.unit }}</p>
      <p v-if="a.text" dir="auto" class="mx-auto mt-[8px] max-w-[280px] text-[14px] leading-[20px] text-[#3e546c]">{{ a.text }}</p>
    </div>
  </section>

  <section v-else-if="type === 'roundList' && a.items?.length">
    <p v-if="a.title" dir="auto" class="reader-serif mb-[6px] text-[20px] font-bold text-[#1c4848]">{{ a.title }}</p>
    <div v-for="(item, i) in a.items" :key="i" class="flex items-center gap-[14px] py-[10px]">
      <img v-if="item.image" :src="item.image" alt="" class="h-[52px] w-[52px] shrink-0 rounded-full object-cover shadow-[0_8px_16px_-6px_rgba(28,72,72,0.5)]" loading="lazy">
      <div class="min-w-0 flex-1">
        <p dir="auto" class="text-[15px] font-bold leading-[20px] text-[#1c4848]">{{ item.title }}</p>
        <p v-if="item.text" dir="auto" class="text-[13px] leading-[18px] text-[#3e546c]/75">{{ item.text }}</p>
      </div>
      <p v-if="item.value" dir="auto" class="reader-serif shrink-0 text-[18px] font-bold text-[#c9a961]">{{ item.value }}</p>
    </div>
  </section>

  <section v-else-if="type === 'checklist' && a.items?.length" class="rounded-[26px] bg-white p-[20px] shadow-[0_14px_34px_-14px_rgba(28,72,72,0.3)] ring-1 ring-[#3e546c]/[0.05]">
    <div class="flex items-baseline justify-between gap-[12px]">
      <p v-if="a.title" dir="auto" class="reader-serif text-[19px] font-bold leading-[24px] text-[#1c4848]">{{ a.title }}</p>
      <p class="shrink-0 text-[13px] font-bold tabular-nums text-[#c9a961]">{{ done.length }} / {{ a.items.length }}</p>
    </div>
    <div class="mt-[10px] h-[6px] overflow-hidden rounded-full bg-[#1c4848]/10"><div class="h-full rounded-full bg-gradient-to-r from-[#1c4848] to-[#49a595] transition-all duration-300" :style="{ width: `${(done.length / a.items.length) * 100}%` }"></div></div>
    <button v-for="(item, i) in a.items" :key="i" type="button" class="mt-[8px] flex min-h-[44px] w-full items-start gap-[12px] py-[6px] text-start" :aria-pressed="done.includes(i)" @click="toggle(i)">
      <span class="mt-[1px] flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border-2 transition-colors" :class="done.includes(i) ? 'border-[#1c4848] bg-[#1c4848] text-white' : 'border-[#1c4848]/25 text-transparent'" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.200" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.500 5 9.500 10 3"/></svg></span>
      <span class="min-w-0"><span dir="auto" class="block text-[15px] font-bold leading-[21px] text-[#1c4848]">{{ item.title }}</span><span v-if="item.text" dir="auto" class="mt-[2px] block text-[14px] leading-[20px] text-[#3e546c]">{{ item.text }}</span></span>
    </button>
  </section>

  <section v-else-if="type === 'curve'" class="overflow-hidden rounded-[28px] pt-[20px]" :class="curve.box">
    <div class="px-[20px]">
      <p v-if="a.kicker" dir="auto" class="text-[11px] font-bold uppercase tracking-[1.200px]" :class="curve.accent">{{ a.kicker }}</p>
      <p class="mt-[4px] flex items-baseline gap-[6px]"><span class="reader-serif text-[40px] font-bold leading-[44px]" :class="curve.accent">{{ a.value }}</span><span dir="auto" class="text-[12px] font-bold uppercase tracking-[1px]" :class="curve.accent">{{ a.unit }}</span></p>
      <p v-if="a.text" dir="auto" class="mt-[4px] text-[14px] leading-[20px]" :class="curve.body">{{ a.text }}</p>
    </div>
    <div class="relative mt-[8px] h-[110px]">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" class="absolute inset-0 h-full w-full" aria-hidden="true">
        <path :d="`${curvePath} L100,100 L0,100 Z`" :fill="curve.fill" />
        <path :d="curvePath" fill="none" :stroke="curve.line" stroke-width="2" vector-effect="non-scaling-stroke" />
      </svg>
      <span v-for="(p, i) in curvePoints" :key="i" class="absolute h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow" :style="{ left: `${p.x}%`, top: `${p.y}%`, background: curve.line }" aria-hidden="true"></span>
    </div>
    <div class="flex px-0 pb-[14px]" :class="curve.under">
      <p v-for="(stop, i) in a.stops" :key="i" dir="auto" class="min-w-0 flex-1 px-[2px] pt-[6px] text-center text-[11px] font-semibold leading-[14px]" :class="curve.label">{{ stop }}</p>
    </div>
  </section>

  <section v-else-if="type === 'action'" class="rounded-[26px] p-[20px]" :class="action.box">
    <div class="flex items-start gap-[14px]">
      <img v-if="a.image" :src="a.image" alt="" class="h-[56px] w-[56px] shrink-0 rounded-full object-cover ring-2 ring-white/40" loading="lazy">
      <div class="min-w-0 flex-1">
        <p v-if="a.kicker" dir="auto" class="text-[11px] font-bold uppercase tracking-[1.200px]" :class="action.accent">{{ a.kicker }}</p>
        <p v-if="a.title" dir="auto" class="reader-serif mt-[3px] text-[18px] font-bold leading-[23px]" :class="action.ink">{{ a.title }}</p>
        <p v-if="a.text" dir="auto" class="mt-[4px] whitespace-pre-line text-[14px] leading-[20px]" :class="action.body">{{ a.text }}</p>
      </div>
    </div>
    <a v-if="a.label && a.url" :href="a.url" target="_blank" rel="noopener noreferrer" class="mt-[14px] flex min-h-[46px] items-center justify-center rounded-full px-[18px] text-[14px] font-bold" :class="action.button">{{ a.label }}</a>
  </section>

  <section v-else-if="type === 'pins'">
    <p v-if="a.title" dir="auto" class="reader-serif mb-[10px] text-[20px] font-bold text-[#1c4848]">{{ a.title }}</p>
    <div class="relative aspect-[3/4] overflow-hidden rounded-[28px] bg-[#1c4848]/10 shadow-[0_16px_34px_-18px_rgba(28,72,72,0.5)]">
      <img v-if="a.image" :src="a.image" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy">
      <button v-for="(item, i) in a.items" :key="i" type="button" class="absolute flex h-[44px] w-[44px] -translate-x-1/2 -translate-y-1/2 items-center justify-center" :style="{ left: `${item.x}%`, top: `${item.y}%` }" :aria-label="item.title" :aria-pressed="pin === i" @click="pin = i">
        <span class="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 text-[13px] font-bold shadow-lg transition-colors" :class="pin === i ? 'border-white bg-[#c9a961] text-white' : 'border-white bg-white/85 text-[#1c4848]'">{{ i + 1 }}</span>
      </button>
      <div v-if="a.items?.[pin]" class="glass-dark absolute inset-x-[12px] bottom-[12px] rounded-[20px] px-[16px] py-[12px]">
        <p dir="auto" class="reader-serif text-[17px] font-bold leading-[22px] text-white">{{ a.items[pin].title }}</p>
        <p v-if="a.items[pin].text" dir="auto" class="mt-[2px] text-[14px] leading-[20px] text-white/90">{{ a.items[pin].text }}</p>
      </div>
    </div>
  </section>

  <section v-else-if="type === 'spotlight'" class="rounded-[28px] bg-gradient-to-b from-[#e8f3f4] to-[#f7fbfa] p-[20px]">
    <div class="relative mx-auto my-[12px] flex h-[210px] w-[210px] items-center justify-center rounded-full bg-[#1c4848]/[0.05]">
      <div class="flex h-[172px] w-[172px] items-center justify-center rounded-full bg-[#1c4848]/[0.07]">
        <img v-if="a.image" :src="a.image" alt="" class="h-[136px] w-[136px] rounded-full object-cover shadow-[0_14px_24px_-10px_rgba(28,72,72,0.6)]" loading="lazy">
      </div>
    </div>
    <p v-if="a.title" dir="auto" class="reader-serif mt-[6px] text-[26px] font-bold leading-[32px] text-[#1c4848]">{{ a.title }}</p>
    <p v-if="a.meta" dir="auto" class="text-[13px] font-medium text-[#3e546c]/70">{{ a.meta }}</p>
    <div v-if="a.items?.length" class="mt-[12px] flex gap-[24px]">
      <div v-for="(item, i) in a.items" :key="i"><p dir="auto" class="text-[17px] font-bold leading-[22px] text-[#1c4848]">{{ item.value }}</p><p dir="auto" class="text-[11px] uppercase tracking-[0.800px] text-[#3e546c]/70">{{ item.label }}</p></div>
    </div>
    <p v-if="a.text" dir="auto" class="mt-[12px] whitespace-pre-line text-[15px] leading-[22px] text-[#3e546c]">{{ a.text }}</p>
  </section>

  <section v-else-if="type === 'statement'" class="overflow-hidden rounded-[28px] bg-[#0a1433] shadow-[0_18px_36px_-16px_rgba(10,20,51,0.6)]">
    <div v-if="a.image" class="h-[170px]" style="-webkit-mask-image:linear-gradient(#000 45%,transparent);mask-image:linear-gradient(#000 45%,transparent)"><img :src="a.image" alt="" class="h-full w-full object-cover" loading="lazy"></div>
    <div class="px-[22px] pb-[24px]" :class="a.image ? '-mt-[10px]' : 'pt-[24px]'">
      <p dir="auto" class="reader-serif relative text-[28px] font-bold leading-[35px] text-white">{{ titleParts[0] }}<span class="text-[#f2c879]">{{ titleParts[1] }}</span>{{ titleParts[2] }}</p>
      <p v-if="a.text" dir="auto" class="mt-[10px] whitespace-pre-line text-[15px] leading-[22px] text-white/75">{{ a.text }}</p>
    </div>
  </section>

  <section v-else-if="type === 'deck' && a.items?.length">
    <div v-for="(item, i) in a.items" :key="i" class="relative rounded-[26px] p-[20px] shadow-[0_-10px_24px_-14px_rgba(28,52,72,0.4)]" :class="[DECK[i % DECK.length].box, i ? '-mt-[22px]' : '', i < a.items.length - 1 ? 'pb-[42px]' : '']">
      <div class="flex items-start gap-[14px]">
        <div class="min-w-0 flex-1">
          <p v-if="item.kicker" dir="auto" class="text-[11px] font-bold uppercase tracking-[1.200px]" :class="DECK[i % DECK.length].accent">{{ item.kicker }}</p>
          <p v-if="item.title" dir="auto" class="reader-serif mt-[3px] text-[17px] font-bold leading-[23px]" :class="DECK[i % DECK.length].ink">{{ item.title }}</p>
          <p v-if="item.text" dir="auto" class="mt-[4px] text-[14px] leading-[20px]" :class="DECK[i % DECK.length].body">{{ item.text }}</p>
        </div>
        <img v-if="item.image" :src="item.image" alt="" class="h-[64px] w-[64px] shrink-0 rounded-[16px] object-cover" loading="lazy">
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';

const INK = { accent: 'text-[#c0604c]', ink: 'text-[#1c4848]', body: 'text-[#3e546c]' };
const ON_DARK = { accent: 'text-[#ecd79f]', ink: 'text-white', body: 'text-white/85' };
const DECK = [
  { box: 'bg-gradient-to-br from-[#fdeadf] to-[#f6e2ef]', ...INK },
  { box: 'bg-gradient-to-br from-[#dff1f6] to-[#e4e9fb]', ...INK, accent: 'text-[#2a7a8c]' },
  { box: 'bg-gradient-to-br from-[#49a595] to-[#2a6a64]', ...ON_DARK },
  { box: 'bg-gradient-to-br from-[#1c4848] to-[#143838]', ...ON_DARK },
  { box: 'bg-gradient-to-br from-[#20344a] to-[#172638]', ...ON_DARK },
];
const CURVES = {
  peach: { box: 'bg-gradient-to-b from-[#fdeadf] to-[#f6e2ef]', accent: 'text-[#c0604c]', body: 'text-[#3e546c]', fill: 'rgba(255,255,255,0.55)', line: '#d9705c', under: 'bg-white/55', label: 'text-[#3e546c]' },
  sky: { box: 'bg-gradient-to-b from-[#dff1f6] to-[#e4e9fb]', accent: 'text-[#2a7a8c]', body: 'text-[#3e546c]', fill: 'rgba(255,255,255,0.55)', line: '#2a7a8c', under: 'bg-white/55', label: 'text-[#3e546c]' },
  teal: { box: 'bg-gradient-to-b from-[#1c4848] to-[#2a6a64]', accent: 'text-[#ecd79f]', body: 'text-white/85', fill: 'rgba(255,255,255,0.1)', line: '#ecd79f', under: 'bg-white/10', label: 'text-white' },
};
const ACTIONS = {
  dark: { box: 'bg-gradient-to-br from-[#20344a] to-[#172638]', ...ON_DARK, button: 'bg-white text-[#20344a]' },
  teal: { box: 'bg-gradient-to-br from-[#1c4848] to-[#2a6a64]', ...ON_DARK, button: 'bg-white text-[#1c4848]' },
  sky: { box: 'bg-gradient-to-br from-[#dff1f6] to-[#e4e9fb]', ...INK, accent: 'text-[#2a7a8c]', button: 'bg-[#1c4848] text-white' },
  peach: { box: 'bg-gradient-to-br from-[#fdeadf] to-[#f6e2ef]', ...INK, button: 'bg-[#1c4848] text-white' },
};

const props = defineProps({ node: { type: Object, required: true } });

const done = ref([]);
const pin = ref(0);

const type = computed(() => props.node.type);
const a = computed(() => props.node.attrs ?? {});
const curve = computed(() => CURVES[a.value.tone] ?? CURVES.peach);
const action = computed(() => ACTIONS[a.value.tone] ?? ACTIONS.dark);

/** A path that climbs through one point per stop, in step with the labels under it. */
const curvePoints = computed(() => {
  const total = Math.max((a.value.stops ?? []).length, 1);
  return Array.from({ length: total }, (_, i) => {
    const t = (i + 0.5) / total;
    return { x: t * 100, y: 78 - t * 56 + Math.sin(t * Math.PI * 3) * 10 };
  });
});
const curvePath = computed(() => {
  const points = [{ x: 0, y: 84 }, ...curvePoints.value, { x: 100, y: 16 }];
  return points.reduce((path, p, i) => {
    if (i === 0) return `M${p.x},${p.y}`;
    const before = points[i - 1];
    const middle = (before.x + p.x) / 2;
    return `${path} C${middle},${before.y} ${middle},${p.y} ${p.x},${p.y}`;
  }, '');
});

const titleParts = computed(() => {
  const title = a.value.title ?? '';
  const accent = a.value.accent ?? '';
  const at = accent ? title.indexOf(accent) : -1;
  return at < 0 ? [title, '', ''] : [title.slice(0, at), accent, title.slice(at + accent.length)];
});

function toggle(i) {
  done.value = done.value.includes(i) ? done.value.filter((n) => n !== i) : [...done.value, i];
}
</script>

<style scoped>
.reader-serif { font-family: 'New York', ui-serif, Georgia, 'Times New Roman', serif; }
.glass-dark { background: rgba(20, 30, 45, 0.38); -webkit-backdrop-filter: blur(18px) saturate(1.4); backdrop-filter: blur(18px) saturate(1.4); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18); }
</style>
